import type { ReactNode } from 'react';
import localforage from 'localforage';
import StarlingWeb from '@ies/starling_client';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { getQueryString } from '@utils/schema';
import type { LexerHandlerMap, LexerHandlerKey, LexerLoader, extraOptions } from '@services/i18n_lexer/typings';
import replaceLoader from '@services/i18n_lexer/loaders/replace';
import lexerHandler from '@services/i18n_lexer/handler';
import bridge from '@libs/bridge';
import { isIos } from '@utils/tools';

const defaultLoader = {
  replace: replaceLoader.handle,
};

const langMap: Record<string, string> = {
  ko: 'ko-KR',
  kor: 'ko-KR',
  ja: 'ja-JP',
  jpn: 'ja-JP',
  th: 'th-TH',
  tha: 'th-TH',
  pt: 'pt-BR',
  por: 'pt-BR',
  vi: 'vi-VN',
  fil: 'fil-PH',
  id: 'id-ID',
  in: 'id-ID',
  es: 'es-LA',
  ru: 'ru-RU',
  tr: 'tr-TR',
  ms: 'ms-MY',
  de: 'de-DE',
  fr: 'fr-FR',
  pl: 'pl-PL',
  it: 'it-IT',
  uk: 'uk-UA',
  km: 'km-KH',
  my: 'my-MM',
  ro: 'ro-RO',
  nl: 'nl-NL',
  cs: 'cs-CZ',
  el: 'el-GR',
  hu: 'hu-HU',
  sv: 'sv-SE',
  fi: 'fi-Fl',
  zh: 'zh-Hans',
  ur: 'ur-PK',
  he: 'he-IL',
  ar: 'ar',
};

// starling实例
let starlingInstance: StarlingWeb;

// 是否已经重置过fallback
let hasResetLang = false;

const inApp = /lv\/([\d\.]+)/i.test(navigator.userAgent.toLowerCase());
export async function guessLang() {
  let language = getQueryString('pageLang') || getQueryString('language');

  if (!language && inApp) {
    if (isIos) {
      const { data } = await bridge.app.getInfo({ type: ['device'] });
      // @ts-ignore return object  have no lanuage
      // eslint-disable-next-line prefer-destructuring
      language = data?.deviceInfo?.header?.language;
    } else {
      const { data } = await bridge.app.getAppInfo();
      ({ language } = data);
    }
  } else if (!language && !inApp) {
    language = navigator.language.substring(0, 2);
  }

  language = language || 'en';
  let lang = langMap[language];
  if (!lang) {
    lang = language;
    if (/zh-Hant/.test(language)) {
      lang = 'zh-Hant-TW';
    } else if (/zh-hans/i.test(lang)) {
      lang = 'en';
    }
  }
  return lang;
}

export async function guessLanguage() {
  let language = getQueryString('pageLang') || getQueryString('language');

  if (!language && inApp) {
    if (isIos) {
      const { data } = await bridge.app.getInfo({ type: ['device'] });
      // @ts-ignore return object  have no lanuage
      // eslint-disable-next-line prefer-destructuring
      language = data?.deviceInfo?.header?.language;
    } else {
      const { data } = await bridge.app.getAppInfo();
      ({ language } = data);
    }
  } else if (!language && !inApp) {
    language = navigator.language.substring(0, 2);
  }

  language = language || 'en';
  return language;
}

export type I18NTranslations = Record<string, string>;
export async function getStarlingText(): Promise<I18NTranslations> {
  const lang = await guessLang();
  const params = {
    apiKey: 'f6b7a750496f11eaacd567482798e876',
    namespace: ['Front_end', 'MobileClient_words_same'],
    locale: lang,
    zone: 'SG',
    timeout: 8000,
    test: false,
    lazyUpdate: true,
    fallbackLang: ['en'], // 降级语言列表，当前语言没有数据时降级到该列表，从左往右
    zoneHost: 'https://starling-sg.byteoversea.com',
    TEAChannelDomain: 'https://sgali-mcs.byteoversea.com',
    store: {
      set(key: string, val: string) {
        return localforage.setItem(key, val);
      },
      get(key: string) {
        return localforage.getItem(key);
      },
    },
  };
  const starling = starlingInstance || new StarlingWeb(params);
  starlingInstance = starling;

  return new Promise<I18NTranslations>(resolve => {
    starling.load(text => {
      resolve(text);
    });
  });
}

function replace(str: string, obj?: Record<string, string>) {
  if (!obj) {
    return str;
  }

  let newStr = str || '';

  Object.keys(obj).forEach((key: string) => {
    newStr = newStr.replace(`{${key}}`, obj[key]);
  });

  return newStr;
}

/** @deprecated use useTranslateV2 instead! e.g import { useTranslateV2 } from './useTranslate';  */
export function useTranslate() {
  const [translations, setTranslations] = useState<I18NTranslations>();

  const fetchI18n = useCallback(async () => {
    const i18nText = await getStarlingText();
    setTranslations(i18nText);
    // 在 lazy 模式下（lazyUpdate 为 true），调用 load 方法会直接返回本地文案，而不等待拉取最新文案。此时可以通过 waitForRefresh 获取更新后的文案
    starlingInstance.waitForRefresh().then(newI18nText => {
      if (newI18nText) {
        setTranslations(newI18nText);
      }
    });
  }, []);

  useEffect(() => {
    fetchI18n();
  }, [fetchI18n]);

  return useCallback(
    (key: string, obj?: I18NTranslations) => {
      if (!translations) {
        return key;
      }
      return replace(translations[key], obj);
    },
    [translations]
  );
}

export function useTranslateV2(loaders: Partial<Record<LexerHandlerKey, LexerLoader['handle']>> = {}) {
  const [translations, setTranslations] = useState<I18NTranslations>();
  const [fallbackData, setFallbackData] = useState<I18NTranslations>();
  const [language, setLang] = useState<string>('');
  const fetchI18n = useCallback(async () => {
    const [translationsData, lang] = await Promise.all([getStarlingText(), guessLanguage()]);
    setTranslations(translationsData);
    setLang(lang);
    /**
     * waitForRefresh 的返回结果依赖最新调用的 load()
     * 每当“首次”切换新语言时, 缓存中没有文案, 会走到下面 t 函数里的 load 英语文案逻辑
     * 导致 waitForRefresh 返回的结果是英语, 覆盖用户预期的语言, 二次进入页面有缓存就没问题
     * 这里主动将 locale 设置回当前语言, 确保获取正确的文案
     */
    starlingInstance.setConfig({ locale: await guessLang() }); // locale 需要和初始化时传的一致
    starlingInstance.load();
    // 在 lazy 模式下（lazyUpdate 为 true），调用 load 方法会直接返回本地文案，而不等待拉取最新文案。此时可以通过 waitForRefresh 获取更新后的文案
    starlingInstance.waitForRefresh().then(newI18nText => {
      if (newI18nText) {
        setTranslations(newI18nText);
      }
    });
  }, []);

  useEffect(() => {
    fetchI18n();
  }, [fetchI18n]);

  const t: {
    (key: string): string;
    (key: string, obj: LexerHandlerMap): ReactNode;
    (key: string, obj: LexerHandlerMap, options: extraOptions): ReactNode;
  } = useCallback(
    (key, obj?: LexerHandlerMap, options?: extraOptions) => {
      try {
        // 英文兜底
        if (!translations?.[key] && !hasResetLang && navigator.language.substring(0, 2) !== 'en') {
          // 确保实例初始化后再调用
          if (starlingInstance) {
            starlingInstance.setConfig({ locale: 'en' });
            starlingInstance
              .load()
              .then(data => setFallbackData(data))
              .catch(_ => null);
            hasResetLang = true;
          }
        }
      } catch (error) {}
      if (!translations) {
        return key;
      }

      try {
        return lexerHandler(
          translations[key] || fallbackData?.[key] || '',
          language,
          { ...defaultLoader, ...loaders },
          obj,
          options
        );
      } catch (e) {
        // 解析失败的情况下返回源文件
        return translations[key] || fallbackData?.[key] || '';
      }
    },
    [translations, language, fallbackData]
  );

  return t;
}

export function useTranslateLoading(translate: Function) {
  const loading = useMemo(() => translate('data_center_projects') !== 'data_center_projects', [translate]);
  return !loading;
}
